import React, { useState, useRef, useEffect } from 'react';
import classNames from 'classnames';
import { useEnhancedChatStore, EnhancedChatMessage } from './enhanced-store';
import { Journey } from 'src/common/interfaces/journey.interface';
import { LayoutMode } from '../types';
import { ThumbUpIcon, ThumbDownIcon, RefreshIcon, ArrowRightIcon as SendIcon } from '@heroicons/react/outline';
import { useCurrentSubscription } from 'src/store/subscription';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

type Props = {
  opened: boolean;
  journeyUUID: string;
  assistantName?: string;
  personality?: string;
  onSourceClick?: (message: EnhancedChatMessage) => void;
  onDialogClose?: () => void;
  findBlockByContentUuid?: (uuid: string) => any;
  findSectionById?: (id: string) => any;
  layoutMode?: LayoutMode;
  isReadOnly?: boolean;
  visitorEmail?: string;
  userContext?: Record<string, any>;
  journey?: Journey;
  inEditor?: boolean;
};

export const EnhancedChatMessages = ({
  opened,
  journeyUUID,
  assistantName = 'Journey Chat Assistant',
  personality = '',
  onSourceClick,
  onDialogClose,
  findBlockByContentUuid,
  findSectionById,
  layoutMode = 'web',
  isReadOnly = false,
  visitorEmail = '',
  userContext = {},
  journey,
  inEditor = false,
}: Props) => {
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);
  
  const {
    conversationId,
    messages,
    isLoading,
    isThinking,
    error,
    inputValue,
    potentialQuestions,
    conversationMetrics,
    isInitialized,
    isInitializing,
    disabled,
    
    startConversation,
    sendMessage,
    setInputValue,
    clearError,
    initializeFromJourney,
  } = useEnhancedChatStore();

  const { features } = useFeatureRestrictions();
  const { renderMode } = useEnhancedChatStore();

  // Initialize the enhanced chat store with journey data when journey is provided
  useEffect(() => {
    if (journey) {
      initializeFromJourney(journey);
    }
  }, [journey, initializeFromJourney]);

  // Determine the effective render mode
  const effectiveRenderMode = renderMode || (inEditor ? 'editor' : 'player');

  // Determine if chatbot should be disabled
  // In player mode, only respect the journey's disabled setting
  // In editor mode, also check feature restrictions
  const shouldBeDisabled = disabled || (effectiveRenderMode === 'editor' && !features.canAccessAIChatbot);
  
  // Debug logging
  console.log('EnhancedChatMessages Debug:', {
    disabled,
    canAccessAIChatbot: features.canAccessAIChatbot,
    renderMode,
    effectiveRenderMode,
    inEditor,
    journey: !!journey,
    shouldBeDisabled,
    isInitialized,
    isInitializing,
    conversationId,
    opened,
    messages: messages.length
  });

  // Initialize conversation when component opens (only if not disabled)
  useEffect(() => {
    if (opened && journeyUUID && !shouldBeDisabled && !isInitializing) {
      // Start conversation if not already initialized or if we need to force a new conversation
      if (!isInitialized || !conversationId) {
        console.log('Starting conversation for neue mode:', { journeyUUID, visitorEmail, userContext });
        startConversation(journeyUUID, visitorEmail, userContext);
      }
    }
  }, [opened, journeyUUID, visitorEmail, userContext, startConversation, shouldBeDisabled, isInitialized, isInitializing, conversationId]);

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (opened && !isThinking) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [opened, isThinking]);
  
  // Removed automatic click-outside close behavior to prevent unintended panel closures

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const message = inputValue.trim();
    if (message && !isThinking) {
      sendMessage(message);
    }
  };

  const handlePredefinedQuestion = (question: string) => {
    if (!isThinking) {
      sendMessage(question);
    }
  };

  const handleSourceClick = (message: EnhancedChatMessage) => {
    const { source_content_uuid, source_node_uuid } = message.metadata || {};
    
    if (source_content_uuid && findBlockByContentUuid) {
      const block = findBlockByContentUuid(source_content_uuid);
      if (block) {
        // Handle block navigation
        onSourceClick?.(message);
      }
    } else if (source_node_uuid && findSectionById) {
      const section = findSectionById(source_node_uuid);
      if (section) {
        // Handle section navigation
        onSourceClick?.(message);
      }
    }
  };


  const renderMessage = (message: EnhancedChatMessage, index: number) => {
    const isUser = message.role === 'user';
    const hasSource = !!(message.metadata?.source_content_uuid || message.metadata?.source_node_uuid);
    const confidenceScore = message.metadata?.confidence_score || 0;
    
    return (
      <div
        key={message.id}
        className={classNames('mb-4 flex animate-fadeIn', {
          'justify-end': isUser,
          'justify-start': !isUser,
        })}
      >
        {!isUser && (
          <div className="w-8 h-8 rounded-full bg-neue-journey-accent-10 flex items-center justify-center mr-2 flex-shrink-0">
            <span className="text-neue-journey-accent text-xs font-medium">AI</span>
          </div>
        )}
        <div
          className={classNames('max-w-[80%] rounded-2xl px-4 py-3 shadow-sm', {
            'bg-journey-primary-blue text-journey-white': isUser,
            'bg-neue-journey-bg border border-neue-journey-fg-10 text-neue-journey-fg': !isUser,
          })}
        >
          <div className="whitespace-pre-wrap break-words leading-relaxed">
            {message.content}
          </div>
          
          {/* Source and confidence information */}
          {!isUser && hasSource && (
            <div className="mt-3 text-xs opacity-75 border-t border-neue-journey-fg-10 pt-2">
              {/* {confidenceScore > 0 && (
                <div className="mb-1 flex items-center">
                  <div className="w-full bg-neue-journey-fg-10 rounded-full h-1.5 mr-2">
                    <div 
                      className="bg-neue-journey-accent h-1.5 rounded-full" 
                      style={{ width: `${Math.round(confidenceScore * 100)}%` }}
                    ></div>
                  </div>
                  <span>Confidence: {Math.round(confidenceScore * 100)}%</span>
                </div>
              )} */}
              <button
                onClick={() => handleSourceClick(message)}
                className="text-neue-journey-accent hover:text-neue-journey-accent/80 font-medium flex items-center gap-1 transition-colors duration-200"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                View source in Journey
              </button>
            </div>
          )}
          
          {/* Feedback buttons for assistant messages */}
          {/* {!isUser && !isReadOnly && (
            <div className="mt-2 flex items-center gap-2">
              <button
                onClick={() => handleFeedback(message.id, 'thumbs_up')}
                className={classNames('p-1.5 rounded-full hover:bg-neue-journey-accent-10 transition-colors duration-200', {
                  'bg-bedrock-green/20 text-bedrock-green': message.feedback?.type === 'thumbs_up',
                  'text-neue-journey-fg-50': message.feedback?.type !== 'thumbs_up',
                })}
                title="Helpful"
              >
                <ThumbUpIcon className="w-3.5 h-3.5" />
              </button>
              <button
                onClick={() => handleFeedback(message.id, 'thumbs_down')}
                className={classNames('p-1.5 rounded-full hover:bg-neue-journey-accent-10 transition-colors duration-200', {
                  'bg-bedrock-red/20 text-bedrock-red': message.feedback?.type === 'thumbs_down',
                  'text-neue-journey-fg-50': message.feedback?.type !== 'thumbs_down',
                })}
                title="Not helpful"
              >
                <ThumbDownIcon className="w-3.5 h-3.5" />
              </button>
            </div>
          )} */}
          
          <div className="mt-1 text-xs opacity-50">
            {new Date(message.timestamp).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
          </div>
        </div>
        {isUser && (
          <div className="w-8 h-8 rounded-full bg-journey-primary-blue flex items-center justify-center ml-2 flex-shrink-0">
            <span className="text-journey-white text-xs font-medium">You</span>
          </div>
        )}
      </div>
    );
  };

  if (!opened) {
    return null;
  }

  return (
    <div 
      ref={chatContainerRef}
      className="flex flex-col h-full bg-neue-journey-bg-90 rounded-lg shadow-lg overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between p-4 bg-neue-journey-bg border-b shadow-sm">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-neue-journey-accent-10 flex items-center justify-center">
            <span className="text-neue-journey-accent text-xs font-medium">AI</span>
          </div>
          <div>
            <h3 className="font-semibold text-neue-journey-fg">{assistantName}</h3>
            {conversationMetrics.messageCount > 0 && (
              <div className="text-xs text-neue-journey-fg-50">
                {conversationMetrics.messageCount} messages
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2">
   
          <button
            onClick={onDialogClose}
            className="p-2 rounded-full hover:bg-neue-journey-fg-10 transition-colors duration-200"
            title="Close chat"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 text-neue-journey-fg-50" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
            </svg>
          </button>
        </div>
      </div>

      {/* Error display */}
      {error && (
        <div className="p-4 bg-bedrock-red/10 border-b animate-fadeIn">
          <div className="flex items-center justify-between">
            <div className="text-bedrock-red text-sm flex items-center gap-2">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {error}
            </div>
            <button
              onClick={clearError}
              className="text-bedrock-red hover:text-bedrock-red/80 p-1 rounded-full hover:bg-bedrock-red/10 transition-colors duration-200"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto p-4 space-y-4">
        {shouldBeDisabled ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-neue-journey-fg-50 flex flex-col items-center">
              <div className="mb-4">
                <svg className="h-12 w-12 text-neue-journey-fg-30" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728L5.636 5.636m12.728 12.728L18.364 5.636M5.636 18.364l12.728-12.728" />
                </svg>
              </div>
              <span className="text-lg font-medium text-neue-journey-fg">Chatbot is disabled</span>
              <span className="mt-2 text-sm text-neue-journey-fg-30 text-center">The chatbot is currently turned off. Enable it in the settings to start chatting.</span>
            </div>
          </div>
        ) : isInitializing ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-neue-journey-fg-50 flex flex-col items-center">
              <div className="mb-4">
                <svg className="animate-spin h-8 w-8 text-neue-journey-accent" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
              </div>
              <span>Initializing conversation...</span>
              <span className="mt-2 text-sm text-neue-journey-fg-30">Give me a couple seconds. Getting up to date information</span>
            </div>
          </div>
        ) : (
          <>
            {messages.map(renderMessage)}
            {isThinking && (
              <div className="mb-4 flex justify-start animate-fadeIn">
                <div className="w-8 h-8 rounded-full bg-neue-journey-accent-10 flex items-center justify-center mr-2 flex-shrink-0">
                  <span className="text-neue-journey-accent text-xs font-medium">AI</span>
                </div>
                <div className="bg-neue-journey-bg border border-neue-journey-fg-10 rounded-2xl px-4 py-3 shadow-sm">
                  <div className="flex items-center gap-2">
                    <div className="flex gap-1">
                      <div className="w-2 h-2 bg-neue-journey-accent/40 rounded-full animate-bounce" />
                      <div className="w-2 h-2 bg-neue-journey-accent/70 rounded-full animate-bounce delay-100" />
                      <div className="w-2 h-2 bg-neue-journey-accent rounded-full animate-bounce delay-200" />
                    </div>
                    <span className="text-sm text-neue-journey-fg-50">{assistantName} is thinking...</span>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </>
        )}
      </div>

      {/* Potential Questions */}
      {potentialQuestions.length > 0 && !isThinking && !shouldBeDisabled && (
        <div className="p-4 border-t bg-neue-journey-bg animate-fadeIn">
          <div className="text-xs font-medium text-neue-journey-fg-50 mb-2 flex items-center gap-1">
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Suggested questions:
          </div>
          <div className="flex flex-wrap gap-2">
            {potentialQuestions.map((question, index) => (
              <button
                key={index}
                onClick={() => handlePredefinedQuestion(question)}
                className="px-3 py-1.5 text-sm bg-neue-journey-bg border border-neue-journey-fg-20 rounded-full hover:bg-neue-journey-accent-10 hover:border-neue-journey-accent hover:text-neue-journey-accent transition-all duration-200 shadow-sm"
              >
                {question}
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Input */}
      {!isReadOnly && (
        <div className="p-4 border-t bg-neue-journey-bg">
          <form onSubmit={handleSubmit} className="flex gap-2">
            <input
              ref={inputRef}
              type="text"
              value={inputValue}
              onChange={(e) => setInputValue(e.target.value)}
              placeholder={shouldBeDisabled ? "Chatbot is disabled" : `Ask ${assistantName} about this Journey...`}
              className="flex-1 px-4 py-3 border border-neue-journey-fg-20 text-gray-800 rounded-full shadow-sm transition-all duration-200 disabled:bg-neue-journey-fg-10 disabled:text-neue-journey-fg-30 disabled:cursor-not-allowed"
              disabled={isThinking || !isInitialized || shouldBeDisabled}
            />
            <button
              type="submit"
              disabled={!inputValue.trim() || isThinking || !isInitialized || shouldBeDisabled}
              className="px-4 py-3 bg-journey-primary-blue text-journey-white rounded-full hover:bg-journey-primary-blue/90 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 transition-colors duration-200 shadow-sm disabled:bg-neue-journey-fg-20 disabled:text-neue-journey-fg-30"
            >
              <SendIcon className="w-4 h-4" />
              Send
            </button>
          </form>
        </div>
      )}
    </div>
  );
}; 